import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import React, { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { Segmented } from '@/components/ui/Segmented';
import { useThemePalette } from '@/config/theme';
import { bzzoiroCutout, type MatchPlayer, type TeamRef } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

import { PlayerDetailSheet } from './PlayerDetailSheet';

type Side = 'home' | 'away';

/**
 * Rating chip tint per the panel's scale: ≥7.5 green / 6.5–7.5 neutral accent /
 * <6.5 dim. Returns palette-aware colours so it reads on light + dark.
 */
function ratingTone(rating: number, palette: ReturnType<typeof useThemePalette>) {
  if (rating >= 7.5) return { bg: palette.win, fg: '#04140B' };
  if (rating >= 6.5) return { bg: palette.voltGlassStrong, fg: palette.voltBright };
  return { bg: palette.surface2, fg: palette.muted };
}

function oneDecimal(value: number): string {
  return (Math.round(value * 10) / 10).toFixed(1);
}
function twoDecimals(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2);
}

function RatingChip({ rating, large }: { rating: number; large?: boolean }) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  const tone = ratingTone(rating, palette);
  return (
    <View
      className="items-center justify-center rounded-md"
      style={{
        backgroundColor: tone.bg,
        width: large ? 46 : 38,
        height: large ? 32 : 26,
      }}
    >
      <AppText weight="bold" tabular style={{ color: tone.fg, fontSize: large ? 16 : 13 }}>
        {localizeDigits(oneDecimal(rating), arabicNumerals)}
      </AppText>
    </View>
  );
}

/**
 * The avatar source chain for a rated player: the Bzzoiro transparent cut-out
 * first (resolved from `provider_player_id`), then the stored photo, then (in
 * PlayerPhoto) the initials. provider_player_id flows from get_match_xg /
 * match_player_stats, so these ids are always Bzzoiro-sourced here.
 */
function avatarSources(player: MatchPlayer): (string | null)[] {
  return [bzzoiroCutout(player.provider_player_id), player.photo_url];
}

/** Two-letter initials from a player's name, for the no-photo fallback. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

/**
 * Circular player headshot (365-style) with a graceful source fallback. `sources`
 * is an ORDERED list of candidate URLs (e.g. the Bzzoiro cut-out first, then the
 * stored photo); the component shows the first that loads and advances to the
 * next on error, finally landing on the player's initials when every source
 * fails or the list is empty. A thin team-tinted ring frames the photo so the
 * row still reads which side they're on.
 */
function PlayerPhoto({
  sources,
  name,
  size,
  ringColor,
}: {
  sources: (string | null | undefined)[];
  name: string;
  size: number;
  ringColor: string;
}) {
  // The de-duplicated, non-empty candidate chain (cut-out → photo → …). A stable
  // string `key` over its URLs lets us detect a *real* change (recycled row →
  // different player) without depending on the parent passing a new array each
  // render (it does — `sources` is rebuilt every render).
  const { candidates, key } = React.useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const src of sources) {
      if (src && !seen.has(src)) {
        seen.add(src);
        out.push(src);
      }
    }
    return { candidates: out, key: out.join('|') };
  }, [sources]);

  // Which candidate we're currently attempting; on error we step to the next.
  const [index, setIndex] = React.useState(0);
  // When the chain's identity changes, reset to the best source DURING render —
  // the React-recommended alternative to a reset effect (no cascading re-render).
  const [seenKey, setSeenKey] = React.useState(key);
  if (seenKey !== key) {
    setSeenKey(key);
    setIndex(0);
  }

  const uri = index < candidates.length ? candidates[index] : null;
  const fontSize = Math.round(size * 0.36);

  return (
    <View
      className="items-center justify-center overflow-hidden rounded-full bg-surface-2"
      style={{ width: size, height: size, borderWidth: 1.5, borderColor: ringColor }}
    >
      {uri ? (
        <Image
          // Keyed by the active URI so a fall-through actually re-mounts onto the
          // next source instead of caching the failed one.
          key={uri}
          source={{ uri }}
          style={{ width: size, height: size }}
          contentFit="cover"
          contentPosition="top"
          transition={150}
          cachePolicy="memory-disk"
          onError={() => setIndex((current) => current + 1)}
          accessibilityLabel={name}
        />
      ) : (
        <AppText weight="bold" className="text-soft" style={{ fontSize }}>
          {initialsOf(name)}
        </AppText>
      )}
    </View>
  );
}

/** A compact "label value" stat token used in a player's row + detail strip. */
function StatToken({ label, value }: { label: string; value: string }) {
  return (
    <View className="flex-row items-center gap-1">
      <AppText variant="micro" weight="bold" tabular className="text-soft">
        {value}
      </AppText>
      <AppText variant="micro" className="text-muted">
        {label}
      </AppText>
    </View>
  );
}


function PlayerRow({
  player,
  first,
  onOpenProfile,
}: {
  player: MatchPlayer;
  first: boolean;
  /** Open the full match-performance sheet for this player. */
  onOpenProfile: () => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const num = (value: number) => localizeDigits(String(value), arabicNumerals);
  const sideColor = player.team_side === 'home' ? palette.volt : palette.teamAway;

  return (
    <View className={first ? '' : 'border-t border-line'}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('match.playerDetail.openProfile', { name: player.player_name })}
        onPress={onOpenProfile}
        className="flex-row items-center gap-3 px-4 py-3 active:bg-surface-2/50"
      >
        <PlayerPhoto sources={avatarSources(player)} name={player.player_name} size={44} ringColor={sideColor} />
        <View className="flex-1">
          <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
            {player.player_name}
          </AppText>
          <View className="flex-row flex-wrap items-center gap-x-3 gap-y-0.5 pt-0.5">
            {player.position ? (
              <AppText variant="micro" weight="medium" className="text-muted">
                {player.position}
              </AppText>
            ) : null}
            <StatToken label={t('common.min')} value={num(player.minutes)} />
            {player.goals > 0 ? <StatToken label="⚽" value={num(player.goals)} /> : null}
            {player.assists > 0 ? <StatToken label="🅰" value={num(player.assists)} /> : null}
            {player.xg > 0 ? (
              <StatToken
                label={t('match.xgShort')}
                value={localizeDigits(twoDecimals(player.xg), arabicNumerals)}
              />
            ) : null}
            {player.key_passes > 0 ? (
              <StatToken label={t('match.ratingKeyPasses')} value={num(player.key_passes)} />
            ) : null}
          </View>
        </View>
        <RatingChip rating={player.rating ?? 0} />
        <Ionicons name="chevron-forward" size={15} color={palette.muted} />
      </Pressable>
    </View>
  );
}

/** The single highest-rated player across both teams, as a slim highlight. */
function StarMan({
  player,
  teamName,
}: {
  player: MatchPlayer;
  teamName: string;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  return (
    <Card elevated className="overflow-hidden">
      <View
        className="flex-row items-center gap-3.5 px-4 py-3.5"
        style={{ borderLeftWidth: 3, borderLeftColor: palette.captain }}
      >
        <PlayerPhoto
          sources={avatarSources(player)}
          name={player.player_name}
          size={60}
          ringColor={palette.captain}
        />
        <View className="flex-1">
          <AppText
            variant="micro"
            weight="bold"
            className="uppercase tracking-widest"
            style={{ color: palette.captain }}
          >
            {t('match.starMan')}
          </AppText>
          <AppText variant="body" weight="bold" className="text-ink" numberOfLines={1}>
            {player.player_name}
          </AppText>
          <AppText variant="micro" className="text-soft" numberOfLines={1}>
            {teamName}
            {player.position ? ` · ${player.position}` : ''}
            {player.goals > 0 ? ` · ${localizeDigits(String(player.goals), arabicNumerals)}⚽` : ''}
            {player.assists > 0
              ? ` · ${localizeDigits(String(player.assists), arabicNumerals)}🅰`
              : ''}
          </AppText>
        </View>
        <RatingChip rating={player.rating ?? 0} large />
      </View>
    </Card>
  );
}

/**
 * Player ratings: a "Star man" highlight (top rating across both teams), a
 * Home/Away toggle, then that side's rated players (best first) with a colour-
 * scaled chip and compact stats. Tapping a row opens the full profile sheet when
 * the player is resolvable (has a provider id); otherwise it expands an inline
 * detail strip from the provider's `raw` blocks. Only players with a non-null
 * rating are listed; the caller hides this whole block when no player has a
 * rating yet.
 */
export function XgPlayerRatings({
  players,
  homeTeam,
  awayTeam,
}: {
  players: MatchPlayer[];
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [side, setSide] = useState<Side>('home');
  // The open match-performance sheet: the tapped player's full row, or null.
  const [selected, setSelected] = useState<MatchPlayer | null>(null);

  const { rated, star, starTeam } = useMemo(() => {
    const withRating = players.filter((p) => p.rating != null);
    const sorted = withRating
      .slice()
      .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0) || b.minutes - a.minutes);
    const best = sorted[0] ?? null;
    const team = best
      ? localizeTeamName(best.team_side === 'home' ? homeTeam : awayTeam, locale)
      : '';
    return { rated: withRating, star: best, starTeam: team };
  }, [players, homeTeam, awayTeam, locale]);

  const sidePlayers = useMemo(
    () =>
      rated
        .filter((p) => p.team_side === side)
        .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0) || b.minutes - a.minutes),
    [rated, side],
  );

  if (rated.length === 0) return null;

  // A row's stable key (name + minute is unique enough across a single side).
  const rowKey = (player: MatchPlayer) => `${player.team_side}-${player.player_name}-${player.minutes}`;

  return (
    <View className="gap-3">
      <View className="flex-row items-center justify-between px-1">
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('match.playerRatings')}
        </AppText>
        <AppText variant="micro" weight="medium" className="text-muted">
          {t('match.tapForMore')}
        </AppText>
      </View>
      {star ? <StarMan player={star} teamName={starTeam} /> : null}
      <Segmented
        options={[
          { value: 'home', label: localizeTeamName(homeTeam, locale) },
          { value: 'away', label: localizeTeamName(awayTeam, locale) },
        ]}
        value={side}
        onChange={setSide}
      />
      <Card className="overflow-hidden">
        {sidePlayers.map((player, index) => (
          <PlayerRow
            key={rowKey(player)}
            player={player}
            first={index === 0}
            onOpenProfile={() => setSelected(player)}
          />
        ))}
      </Card>
      <PlayerDetailSheet player={selected} onClose={() => setSelected(null)} />
    </View>
  );
}
